import { Component, OnDestroy } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { ToolroomCutterLabel, ToolroomFirstImage, ToolroomMachineStatus, ToolroomMinutes } from "@helpers/toolroomMachineStatus";
import { ToolroomJobProgress } from "@helpers/toolroomProgress";
import { MachineService } from "@services/machine.service";

// A wall board for the toolroom: every machine's status, the job on it, how far through it is and which cutter
// should be in - the same picture the operator terminal shows at the machine. Kiosk page: /admin/toolroomOverview/{siteId}
// (0 for every site), no sign-in, refreshed every 15 seconds.
@Component({
	moduleId: module.id,
	selector: "toolroomOverview",
	styleUrls: ["toolroomOverview.css"],
	templateUrl: "toolroomOverview.html"
})
export class ToolroomOverviewComponent implements OnDestroy {
	private alerted = new Set<string>();
	cards: any[] = [];
	private clockTimer: any;
	error = "";
	isLoading = true;
	lastRefresh: Date | null = null;
	now = new Date();
	private refreshTimer: any;
	siteId = 0;
	summary = { down: 0, idle: 0, running: 0, settingUp: 0, supervisor: 0 };

	constructor(private route: ActivatedRoute, private machineService: MachineService) {
		this.route.params.subscribe((params) => {
			this.siteId = Number(params.id) || 0;
			this.isLoading = true;
			this.load();
		});
		this.refreshTimer = setInterval(() => this.load(), 15000);
		// The countdowns move between refreshes.
		this.clockTimer = setInterval(() => {
			this.now = new Date();
			this.cards = this.cards.map((card) => this.decorate(card.raw));
		}, 15000);
	}

	// Everything a card shows, worked out once per refresh rather than in template calls.
	decorate(machine: any): any {
		const status = ToolroomMachineStatus(machine.productionStatus);
		const job = machine.current;
		const progress = job ? ToolroomJobProgress(job.dateStarted, job.cutters, job.estimatedRunTime, this.now) : null;
		const cutter = progress && progress.currentIndex >= 0 ? job.cutters[progress.currentIndex] : null;

		return {
			billetImage: job ? job.billetImageUrl || ToolroomFirstImage(job.toolImageUrls) : "",
			cutter,
			cutterLabel: ToolroomCutterLabel(cutter),
			finish: progress ? progress.finishAt : null,
			finishOtherDay: progress ? progress.finishAt.toDateString() !== this.now.toDateString() : false,
			job,
			machine,
			progress,
			raw: machine,
			requestAge: machine.openRequest ? ToolroomMinutes((this.now.getTime() - new Date(machine.openRequest.requestOpened).getTime()) / 60000) || "just now" : "",
			runningFor: job?.dateStarted ? ToolroomMinutes((this.now.getTime() - new Date(job.dateStarted).getTime()) / 60000) : "",
			settingUpFor: job?.setupStartTime ? ToolroomMinutes((this.now.getTime() - new Date(job.setupStartTime).getTime()) / 60000) : "",
			status,
		};
	}

	load() {
		this.machineService.app_ToolroomOverview(this.siteId)
			.subscribe(
				(res: any) => {
					const machines: any[] = res.response || [];
					this.now = new Date();
					// Running machines with a job on them first (top left), then any other machine with a job on it, then
					// machines with only a queue, then empty ones; cell order within each group.
					const hasJob = (m: any) => !!m.current || m.queueCount > 0;
					const rank = (m: any) => (m.productionStatus === 10 && hasJob(m) ? 0 : m.current ? 1 : hasJob(m) ? 2 : 3);
					const sorted = [...machines].sort((a, b) =>
						rank(a) - rank(b) || (a.cellNumber || "").localeCompare(b.cellNumber || "", undefined, { numeric: true }));
					this.cards = sorted.map((m) => this.decorate(m));
					this.summarise();
					this.soundAlerts(machines);
					this.error = "";
					this.isLoading = false;
					this.lastRefresh = new Date();
				},
				(err) => {
					console.log(err);
					this.error = this.cards.length ? "Can't reach FLOW - showing the last update." : (typeof err === "string" && err ? err : "Can't reach FLOW.");
					this.isLoading = false;
				}
			);
	}

	minutes(value: number): string {
		return ToolroomMinutes(value);
	}

	ngOnDestroy() {
		clearInterval(this.refreshTimer);
		clearInterval(this.clockTimer);
	}

	private play(src: string) {
		try {
			const audio = new Audio(src);
			audio.play().catch(() => console.log("Alert sound blocked until the page is clicked"));
		} catch (e) {
			console.log(e);
		}
	}

	// A new supervisor request or breakdown plays the same alerts as the production overview.
	soundAlerts(machines: any[]) {
		const active = new Set<string>();
		machines.forEach((m) => {
			if (m.openRequest) active.add(`request-${m.openRequest.id}`);
			if (m.productionStatus === 120) active.add(`breakdown-${m.machineId}`);
		});
		if (this.lastRefresh) {
			const fresh = Array.from(active).filter((key) => !this.alerted.has(key));
			if (fresh.some((key) => key.startsWith("request"))) this.play("assets/supervisorRequired.mp3");
			else if (fresh.some((key) => key.startsWith("breakdown"))) this.play("assets/machineBreakdown.mp3");
		}
		this.alerted = active;
	}

	summarise() {
		const tones = this.cards.map((c) => c.status.tone);
		this.summary = {
			down: tones.filter((t) => t === "danger").length,
			idle: tones.filter((t) => t === "idle" || t === "warn").length,
			running: tones.filter((t) => t === "go").length,
			settingUp: tones.filter((t) => t === "setup").length,
			supervisor: this.cards.filter((c) => c.machine.openRequest).length,
		};
	}

	trackMachine(_index: number, card: any) {
		return card.machine.machineId;
	}
}
